function normalizeShopifyImageSrc(src) {
  if (!src || typeof src !== 'string') return src;
  if (src.startsWith('//')) return `https:${src}`;
  if (src.startsWith('http://')) return `https://${src.slice(7)}`;
  return src;
}

function updateResponsiveImage(mainImage, variant) {
  if (!mainImage || !variant?.featured_image?.src) return;

  if (!mainImage._responsiveConfig) {
    const srcset = mainImage.getAttribute('srcset') || '';

    const parsedWidths = srcset
      .split(',')
      .map(item => {
        const match = item.match(/(\d+)w/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter(Boolean)
      .filter((width, index, list) => list.indexOf(width) === index)
      .sort((a, b) => a - b);

    mainImage._responsiveConfig = {
      sizes: mainImage.getAttribute('sizes'),
      widths: parsedWidths,
      width: mainImage.width,
      height: mainImage.height
    };
  }

  const baseUrl = normalizeShopifyImageSrc(variant.featured_image.src).split('?')[0];
  
  const availableWidths = mainImage._responsiveConfig.widths || [];

  const renderedWidth = Math.max(
    Math.ceil(mainImage.clientWidth || mainImage.getBoundingClientRect().width || mainImage._responsiveConfig.width || 0),
    1
  );
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const targetWidth = Math.ceil(renderedWidth * dpr);

  let srcWidth = availableWidths.find(w => w >= targetWidth);
  if (!srcWidth) {
    srcWidth = availableWidths[availableWidths.length - 1] || targetWidth;
  }

  mainImage.src = baseUrl + '?width=' + srcWidth;

  if (availableWidths.length > 0) {
    mainImage.srcset = availableWidths
      .map(w => baseUrl + '?width=' + w + ' ' + w + 'w')
      .join(', ');
  }

  if (mainImage._responsiveConfig.sizes) {
    mainImage.setAttribute('sizes', mainImage._responsiveConfig.sizes);
  }

  mainImage.width = mainImage._responsiveConfig.width;
  mainImage.height = mainImage._responsiveConfig.height;
  mainImage.alt = variant.featured_image.alt || '';
}

function setComplementaryButtonLabel(btn, label) {
  if (!btn || label == null || label === '') return;
  const content = btn.querySelector('.button__content');
  if (content) {
    content.textContent = label;
    if (content.hasAttribute('rotated-text')) content.setAttribute('rotated-text', label);
    if (content.hasAttribute('up-text')) content.setAttribute('up-text', label);
  } else {
    btn.textContent = label;
  }
}

function getPickerMerchReason(picker) {
  return (picker?.dataset?.merchReason || '').trim();
}

function resolveComplementaryMerchState(pickers) {
  const reasons = Array.from(pickers).map(getPickerMerchReason);
  const hasComingSoon = reasons.includes('coming_soon');
  const hasSoldOutTag = reasons.includes('sold_out');
  const hasInventorySoldOut = Array.from(pickers).some((picker) => {
    if (getPickerMerchReason(picker)) return false;
    try {
      const variants = JSON.parse(picker.dataset.variants || '[]');
      const selectedId = parseInt(picker.dataset.selectedVariantId, 10);
      const selected = variants.find((v) => v.id === selectedId);
      return !selected || !selected.available;
    } catch {
      return true;
    }
  });

  return {
    hasComingSoon,
    hasSoldOutTag,
    hasInventorySoldOut,
    isBlocked: hasComingSoon || hasSoldOutTag || hasInventorySoldOut,
    isMerchTag: hasComingSoon || hasSoldOutTag,
  };
}

function syncComplementaryBisVisibility(klaviyoBis, { hasComingSoon, isMerchTag, hasInventorySoldOut }, variant) {
  if (!klaviyoBis) return;
  if (variant?.id) klaviyoBis.dataset.vid = variant.id;
  if (hasComingSoon || isMerchTag) {
    klaviyoBis.classList.remove('show');
  } else if (hasInventorySoldOut) {
    klaviyoBis.classList.add('show');
  } else {
    klaviyoBis.classList.remove('show');
  }
}

function applyComplementaryButtonState(addBtn, popupBody, merchState) {
  if (!addBtn) return;
  const defaultLabel = popupBody?.dataset?.defaultLabel || '';
  const comingSoonLabel = popupBody?.dataset?.comingSoonLabel || '';
  const soldOutLabel = popupBody?.dataset?.soldOutLabel || '';

  addBtn.classList.toggle('is-disabled', merchState.isBlocked);
  addBtn.classList.toggle('is-merch-tag', merchState.isMerchTag);
  if (merchState.isBlocked) {
    addBtn.setAttribute('disabled', 'disabled');
  } else {
    addBtn.removeAttribute('disabled');
  }

  if (merchState.hasComingSoon) {
    setComplementaryButtonLabel(addBtn, comingSoonLabel || defaultLabel);
  } else if (merchState.hasSoldOutTag) {
    setComplementaryButtonLabel(addBtn, soldOutLabel || defaultLabel);
  } else if (!merchState.isBlocked && defaultLabel) {
    setComplementaryButtonLabel(addBtn, defaultLabel);
  }
}

function updateComplementaryAddButton(contextEl, variant) {
  const productContainer = contextEl.closest('.complementary-products-popup .complementary-products-popup__product');
  if (!productContainer) return;
  if (variant.featured_image) {
    const mainImage = productContainer.querySelector('.complementary-products-popup__product-image img');
    updateResponsiveImage(mainImage, variant);
  }
  const popupBody = contextEl.closest('.complementary-products-popup__body');
  if (!popupBody) return;

  const addBtn = popupBody.querySelector('.complementary-products-popup__btn');
  if (!addBtn) return;

  const pickers = popupBody.querySelectorAll('variant-picker-custom');
  const merchState = resolveComplementaryMerchState(pickers);
  applyComplementaryButtonState(addBtn, popupBody, merchState);
  syncComplementaryBisVisibility(popupBody.querySelector('.klaviyo-bis'), merchState, variant);
}

function updateComplementaryAlterAddButton(contextEl, variant) {
  const productContainer = contextEl.closest('.complementary-products-alter-popup');
  if (!productContainer) return;
  if (variant.featured_image) {
    const mainImage = productContainer.querySelector('.complementary-products-popup__product-image img');
    updateResponsiveImage(mainImage, variant);
  }
  const popupBody = contextEl.closest('.complementary-products-popup__body');
  if (!popupBody) return;

  const addBtn = popupBody.querySelector('.complementary-products-alter-popup__btn');
  if (!addBtn) return;

  const pickers = popupBody.querySelectorAll('variant-picker-custom');
  const merchState = resolveComplementaryMerchState(pickers);
  applyComplementaryButtonState(addBtn, popupBody, merchState);
  syncComplementaryBisVisibility(popupBody.querySelector('.klaviyo-bis'), merchState, variant);
}

class VariantPickerCustom extends HTMLElement {
  constructor() {
    super();
    this.variants = JSON.parse(this.dataset.variants);
    this.selectedVariantId = parseInt(this.dataset.selectedVariantId);
    this.selectedOptions = this.getInitialOptions();
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.updateUI();
    this.dispatch('ready', { variantId: this.selectedVariantId });
    const initialVariant = this.findVariant();
    if (initialVariant) {
      this.onVariantChange(initialVariant);
    }
  }

  getInitialOptions() {
    const variant = this.variants.find(v => v.id === this.selectedVariantId);
    return variant ? [variant.option1, variant.option2, variant.option3].filter(Boolean) : [];
  }

  onClick(e) {
    const swatch = e.target.closest('.swatch');

    const container = swatch.closest('.option-container');
    const index = parseInt(container.dataset.optionIndex);
    this.selectedOptions[index] = swatch.dataset.value;

    this.updateSwatchSelection(container, swatch);
    this.updateSelectedValues();

    const variant = this.findVariant();
    if (variant) {
      this.selectedVariantId = variant.id;
      this.dataset.selectedVariantId = variant.id;
      this.updateAvailability();
      this.dispatch('change', { variant, variantId: variant.id, available: variant.available });
      this.onVariantChange(variant);
    }
  }

  onVariantChange(variant) {
    updateComplementaryAddButton(this, variant);
    updateComplementaryAlterAddButton(this, variant);
    if (typeof window.updateCrossSell === 'function') {
      window.updateCrossSell(this, variant);
    }
  }

  updateSwatchSelection(container, selected) {
    container.querySelectorAll('.swatch.active').forEach(s => s.classList.remove('active'));
    selected.classList.add('active');
  }

  updateSelectedValues() {
    this.querySelectorAll('.selected-value').forEach((span, i) => {
      if (this.selectedOptions[i]) span.textContent = this.selectedOptions[i];
    });
  }

  findVariant() {
    return this.variants.find(v =>
      v.option1 === (this.selectedOptions[0] || null) &&
      v.option2 === (this.selectedOptions[1] || null) &&
      v.option3 === (this.selectedOptions[2] || null)
    );
  }

  updateAvailability() {
    const maskInventory = getPickerMerchReason(this) === 'coming_soon';
    this.querySelectorAll('.option-container').forEach((container, i) => {
      container.querySelectorAll('.swatch').forEach(swatch => {
        if (maskInventory) {
          swatch.classList.remove('soldout');
          swatch.dataset.available = 'true';
          return;
        }

        const testOptions = [...this.selectedOptions];
        testOptions[i] = swatch.dataset.value;

        const variant = this.findVariantByOptions(testOptions);
        const available = variant && variant.available;

        swatch.classList.toggle('soldout', !available);
      });
    });
  }

  findVariantByOptions(options) {
    return this.variants.find(v =>
      v.option1 === (options[0] || null) &&
      v.option2 === (options[1] || null) &&
      v.option3 === (options[2] || null)
    );
  }

  updateUI() {
    this.updateSelectedValues();
    this.updateAvailability();

    this.querySelectorAll('.option-container').forEach((container, i) => {
      const value = this.selectedOptions[i];
      const swatch = container.querySelector(`.swatch[data-value="${value}"]`);
      if (swatch) this.updateSwatchSelection(container, swatch);
    });
  }

  setVariant(variantId) {
    const variant = this.variants.find(v => v.id === variantId);
    if (!variant) return;

    this.selectedVariantId = variantId;
    this.dataset.selectedVariantId = variantId;
    this.selectedOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    this.updateUI();
    this.dispatch('change', { variant, variantId: variant.id, available: variant.available });

    this.onVariantChange(variant);
  }

  dispatch(eventName, detail) {
    this.dispatchEvent(new CustomEvent(`variant-${eventName}`, {
      bubbles: true,
      detail
    }));
  }
}

customElements.define('variant-picker-custom', VariantPickerCustom);

window.updateResponsiveImage = updateResponsiveImage;
